import { useEffect, useState } from 'react';
import { accountingGateway } from './index';
import type { ExportMode, ExportOptionsInput, ExportSettings, NetPayDetailMode, WageGroupDimension, WageMappingMode } from './exportTypes';

/**
 * What a person changed for ONE preview or export, on top of the company setting. `null` means "use the company setting" (the server applies it; when no
 * company setting is saved the original rules apply). Only a changed value is ever sent, so an untouched screen sends exactly what it sent before options existed.
 */
export interface OptionOverrides {
  netPay: NetPayDetailMode | null;
  wageMode: Exclude<WageMappingMode, 'LegacyResolver'> | null;
  dimension: WageGroupDimension | null;
}

export const NO_OVERRIDES: OptionOverrides = { netPay: null, wageMode: null, dimension: null };

export type SettingsState = { kind: 'loading' } | { kind: 'ready'; settings: ExportSettings } | { kind: 'unavailable' };

/** The company setting, read once. A person who cannot read settings (or an older server) gets `unavailable`: the screen still works and the server applies the setting. */
export function useExportSettings(reloadKey = 0): SettingsState {
  const [state, setState] = useState<SettingsState>({ kind: 'loading' });
  useEffect(() => {
    const controller = new AbortController();
    accountingGateway.getExportSettings(controller.signal)
      .then((settings) => { if (!controller.signal.aborted) setState({ kind: 'ready', settings }); })
      .catch(() => { if (!controller.signal.aborted) setState({ kind: 'unavailable' }); });
    return () => controller.abort();
  }, [reloadKey]);
  return state;
}

/** The company's saved wage mode (never the legacy one, which is not a setting a person can choose). */
export function companyWageMode(state: SettingsState): Exclude<WageMappingMode, 'LegacyResolver'> | null {
  if (state.kind !== 'ready' || !state.settings.isConfigured) return null;
  return state.settings.wageMappingMode === 'LegacyResolver' ? null : state.settings.wageMappingMode;
}

export const companyNetPay = (state: SettingsState): NetPayDetailMode | null => (state.kind === 'ready' && state.settings.isConfigured ? state.settings.netPayDetailMode : null);
export const companyDimension = (state: SettingsState): WageGroupDimension | null => (state.kind === 'ready' && state.settings.isConfigured ? state.settings.wageGroupDimension : null);

/** What is in force for the net pay choice: the person's change, else the company setting (null: the original rules). */
export const effectiveNetPay = (o: OptionOverrides, state: SettingsState): NetPayDetailMode | null => o.netPay ?? companyNetPay(state);
export const effectiveWageMode = (o: OptionOverrides, state: SettingsState): Exclude<WageMappingMode, 'LegacyResolver'> | null => o.wageMode ?? companyWageMode(state);
export const effectiveDimension = (o: OptionOverrides, state: SettingsState): WageGroupDimension | null =>
  effectiveWageMode(o, state) === 'GroupBased' ? (o.dimension ?? companyDimension(state) ?? 'Department') : null;

/**
 * The option members to send with a preview or an export. Nothing is sent for an untouched choice. A company setting that this person cannot use (per-employee
 * net pay without the permission to view employee compensation) is replaced by the consolidated line for this request, and the screen says so.
 */
export function requestOptions(args: { mode: ExportMode; overrides: OptionOverrides; settings: SettingsState; perEmployeeAllowed: boolean }): ExportOptionsInput {
  const { mode, overrides, settings, perEmployeeAllowed } = args;
  const out: ExportOptionsInput = {};
  if (mode === 'Compound') {
    const chosen = overrides.netPay ?? (companyNetPay(settings) === 'PerEmployee' && !perEmployeeAllowed ? 'Consolidated' : null);
    if (chosen) out.netPayDetailMode = chosen;
  }
  if (overrides.wageMode) {
    out.wageMappingMode = overrides.wageMode;
    if (overrides.wageMode === 'GroupBased') out.wageGroupDimension = overrides.dimension ?? companyDimension(settings) ?? 'Department';
  } else if (overrides.dimension && companyWageMode(settings) === 'GroupBased') {
    out.wageMappingMode = 'GroupBased';
    out.wageGroupDimension = overrides.dimension;
  }
  return out;
}

/** True when the company's per-employee net pay setting cannot be used by this person, so the consolidated line is sent instead. */
export const netPayForcedConsolidated = (o: OptionOverrides, state: SettingsState, perEmployeeAllowed: boolean): boolean =>
  o.netPay === null && companyNetPay(state) === 'PerEmployee' && !perEmployeeAllowed;
